import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy } from "lucide-react";
import { SlotPicker, slotLabel } from "@/components/SlotPicker";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
const Shell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 bg-black/40 z-[70] flex items-end sm:items-center justify-center" onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg p-5 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-start mb-3"><h3 className="text-base font-semibold">{title}</h3><button onClick={onClose} data-testid="dialog-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>
);

export function WhatsAppDraft({ draft, onDone, label = "Send the Meet link on WhatsApp Business from your phone" }) {
  return (
    <div className="space-y-3" data-testid="whatsapp-draft">
      <div className="text-sm font-medium">{label}</div>
      <pre className="whitespace-pre-wrap text-sm bg-[#F6F5EF] rounded-xl p-3 font-sans">{draft}</pre>
      <div className="flex gap-2"><button onClick={() => copy(draft)} data-testid="whatsapp-copy" className="px-4 py-2 rounded-xl bg-[#159A50] text-white text-sm inline-flex items-center gap-2"><Copy size={14} /> Copy message</button><button onClick={onDone} data-testid="whatsapp-done" className="px-4 py-2 rounded-xl border border-[#E2E2E2] text-sm">Done</button></div>
      <div className="text-[11px] text-[#6B7280]">RM Desk never sends WhatsApp.</div>
    </div>
  );
}

// Book a consultation for a lead (board "Match lead" → book, case page, RM use). Pre-Sales callers book through Log call instead.
export function BookDialog({ lead, onClose, onBooked }) {
  const [slot, setSlot] = useState(null);
  const [email, setEmail] = useState(lead.email || "");
  const [noEmail, setNoEmail] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const book = async () => {
    if (!slot) return toast.error("Pick a slot");
    if (!noEmail && !email) return toast.error("Add an e-mail or choose WhatsApp");
    setBusy(true);
    try {
      const r = await api.post("/consult/book", { record_id: lead.record_id, start: slot, email: noEmail ? null : email, no_email: noEmail, notes });
      toast.success(`Booked ${slotLabel(slot)}${noEmail ? "" : " · invite sent"}`);
      onBooked?.(r.data);
      if (noEmail) setDraft(r.data.whatsapp_draft); else onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); } finally { setBusy(false); }
  };
  return (
    <Shell title={`Book consultation · ${lead.name || lead.case_id}`} onClose={onClose} testid="book-dialog">
      {draft ? <WhatsAppDraft draft={draft} onDone={onClose} /> : <>
        <SlotPicker value={slot} onChange={setSlot} />
        <div className="mt-3 space-y-2">
          <label className="text-xs text-[#6B7280]">Customer e-mail (Meet invite)<input value={email} disabled={noEmail} onChange={(e) => setEmail(e.target.value)} data-testid="book-email" className="block w-full border border-[#E2E2E2] rounded-xl px-3 py-2 text-sm text-[#333333] mt-1 disabled:bg-[#F6F5EF]" placeholder="name@example.com" /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={noEmail} onChange={(e) => setNoEmail(e.target.checked)} data-testid="book-no-email" /> No e-mail — I'll send the Meet link on WhatsApp</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the RM (last conversation)" data-testid="book-notes" className="block w-full border border-[#E2E2E2] rounded-xl px-3 py-2 text-sm" />
        </div>
        <button onClick={book} disabled={busy || !slot} data-testid="book-save" className="mt-4 w-full py-3 rounded-xl bg-[#FF4802] text-white text-sm font-medium disabled:opacity-50">{busy ? "Booking…" : slot ? `Book ${slotLabel(slot)}` : "Pick a slot"}</button>
      </>}
    </Shell>
  );
}

export function RescheduleDialog({ event, onClose, onDone }) {
  const [slot, setSlot] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const go = async () => {
    if (!slot) return toast.error("Pick the new slot");
    setBusy(true);
    try {
      const r = await api.post(`/consult/events/${event.id}/reschedule`, { start: slot, note });
      toast.success(`Moved to ${slotLabel(slot)}${event.guest_email ? " · guest notified" : ""}`);
      onDone?.();
      if (!event.guest_email) setDraft(r.data.whatsapp_draft); else onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); } finally { setBusy(false); }
  };
  return (
    <Shell title={`Reschedule · ${event.client}`} onClose={onClose} testid="reschedule-dialog">
      {draft ? <WhatsAppDraft draft={draft} onDone={onClose} label="No e-mail on this booking — send the new time on WhatsApp" /> : <>
        <div className="text-xs text-[#6B7280] mb-2">Currently {slotLabel(event.start)}{event.guest_email ? ` · ${event.guest_email} will get the update` : " · no guest e-mail"}</div>
        <SlotPicker value={slot} onChange={setSlot} />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? (optional, goes to Case History)" data-testid="reschedule-note" className="block w-full border border-[#E2E2E2] rounded-xl px-3 py-2 text-sm mt-3" />
        <button onClick={go} disabled={busy || !slot} data-testid="reschedule-save" className="mt-4 w-full py-3 rounded-xl bg-[#FF4802] text-white text-sm font-medium disabled:opacity-50">{busy ? "Moving…" : "Reschedule"}</button>
      </>}
    </Shell>
  );
}

export function CancelDialog({ event, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [fu, setFu] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) return toast.error("Give a reason");
    setBusy(true);
    try { await api.post(`/consult/events/${event.id}/cancel`, { reason, follow_up: fu || null }); toast.success("Consultation cancelled"); onDone?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } finally { setBusy(false); }
  };
  return (
    <Shell title={`Cancel · ${event.client}`} onClose={onClose} testid="cancel-dialog">
      <div className="text-xs text-[#6B7280] mb-2">{slotLabel(event.start)} — the calendar event is deleted{event.guest_email ? " and the guest is notified" : ""}; the lead goes back to Pre-Sales.</div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="cancel-reason" className="block w-full border border-[#E2E2E2] rounded-xl px-3 py-2 text-sm" />
      <label className="block text-xs text-[#6B7280] mt-2">Next follow-up (optional)<input type="date" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="cancel-follow-up" className="block w-full border border-[#E2E2E2] rounded-xl px-3 py-2 text-sm text-[#333333] mt-1" /></label>
      <button onClick={go} disabled={busy} data-testid="cancel-confirm" className="mt-4 w-full py-3 rounded-xl bg-[#CA1212] text-white text-sm font-medium disabled:opacity-50">{busy ? "Cancelling…" : "Cancel consultation"}</button>
    </Shell>
  );
}
